import React, { useEffect, useState } from 'react';
import {
    AreaChartOutlined,
    BellOutlined,
    CustomerServiceOutlined,
    FileTextOutlined,
    LayoutOutlined,
    LogoutOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import { Badge, Layout, Menu } from 'antd';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { logout } from 'app/slice/authSlice';
import { SiderProps } from 'antd/lib/layout';
import { resetLogin } from 'app/slice/loginSlice';
import questionIcon from 'assets/images/icon_question.png';
import CustomModal from 'components/common/CustomModal';
import ExportIcon from 'assets/icons/svgs/ExportIcon.svg';
import classNames from 'classnames';
import SubMenu from 'antd/lib/menu/SubMenu';

const { Sider } = Layout;
// const { SubMenu } = Menu;

type Props = SiderProps & {
    onCloseCollapsed?: () => void;
    notificationCount: number;
};

const Sidebar = (props: Props) => {
    const { breakpoint, collapsedWidth, collapsed, onCollapse, onCloseCollapsed, notificationCount } = props;

    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [isConfirmLogoutVisible, setIsConfirmLogoutVisible] = useState(false);
    const [selectedKey, setSelectedKey] = useState(pathname);
    const onCloseConfirmLogout = () => setIsConfirmLogoutVisible(false);
    const onOpenConfirmLogout = () => setIsConfirmLogoutVisible(true);
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    /**
     * Close other collapse when one was opened
     *
     * @param {string[]} keys
     */
    const onOpenChange = (keys: string[]) => {
        let newOpenKeys = [];
        let lastKey = keys.pop();
        if (lastKey) newOpenKeys.push(lastKey);

        setOpenKeys(newOpenKeys);
    };

    const onLogout = () => {
        onCloseConfirmLogout();
        navigate('/');
        dispatch(logout());
        dispatch(resetLogin());
    };

    useEffect(() => {
        onCloseCollapsed?.();
    }, [onCloseCollapsed, pathname]);

    useEffect(() => {
        //Default url active menu
        setSelectedKey(pathname);
        if (pathname.includes('/dashboard/kouteki/consultation')) {
            onOpenChange(['dashboard-kouteki']);
            //Set Active list Menu when accessing detail page
            setSelectedKey('/dashboard/kouteki/consultation-list');
        } else if (pathname.includes('/dashboard/working-regulation/meetings')) {
            onOpenChange(['dashboard-wr']);
            //Set Active list Menu when accessing detail page
            setSelectedKey('/dashboard/working-regulation/meetings');
        }
    }, [pathname]);

    return (
        <>
            <Sider
                className={classNames('dashboard-sider sider-menu', breakpoint === 'lg' ? 'mobile' : '')}
                width={300}
                breakpoint={breakpoint}
                collapsedWidth={collapsedWidth}
                collapsed={collapsed}
                onCollapse={onCollapse}
            >
                <Menu
                    className={'menu-layout menu-1'}
                    key={'dashboard-layout'}
                    mode='inline'
                    selectedKeys={[selectedKey]}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                >
                    <Menu.Item className='menu-group-item menu-name-too-long' key='/kouteki'>
                        <NavLink to='/kouteki'>
                            <div className={'menu-group-item-content'}>
                                <span className={'menu-group-item-text'}>公的支援サイトTopへ</span>
                                <img className={'menu-group-item-icon'} width={14} height={14} src={ExportIcon} alt='ExportIcon' />
                            </div>
                        </NavLink>
                    </Menu.Item>
                    <Menu.Item className='menu-group-item menu-name-too-long' key='/kisoku'>
                        <NavLink to='/kisoku'>
                            <div className={'menu-group-item-content'}>
                                <span className={'menu-group-item-text'}>就業規則診断サイトTopへ</span>
                                <img className={'menu-group-item-icon'} width={14} height={14} src={ExportIcon} alt='ExportIcon' />
                            </div>
                        </NavLink>
                    </Menu.Item>
                </Menu>

                <Menu
                    className={'menu-layout menu-2'}
                    key={'dashboard-layout'}
                    mode='inline'
                    selectedKeys={[selectedKey]}
                    defaultOpenKeys={['dashboard-kouteki', 'dashboard-wr']}
                >
                    <Menu.Item key='/dashboard/notifications' icon={<BellOutlined />}>
                        <Link to='/dashboard/notifications'>面談予約更新情報</Link>
                        <Badge className='notification-count' count={notificationCount} />
                    </Menu.Item>
                    <Menu.Item key='/dashboard/calendar' icon={<AreaChartOutlined />}>
                        <Link to='/dashboard/calendar'>ご利用状況</Link>
                    </Menu.Item>
                    <SubMenu
                        key={'dashboard-kouteki'}
                        className={'menu-group'}
                        title={
                            <div className={'menu-group-title'}>
                                <CustomerServiceOutlined />
                                <span className={'menu-group-title-text'}>公的支援メニュー</span>
                            </div>
                        }
                    >
                        <Menu.Item className={'menu-group-item'} key='/dashboard/kouteki/consultation-list'>
                            <NavLink to='/dashboard/kouteki/consultation-list'>面談依頼一覧</NavLink>
                        </Menu.Item>
                        {/* Hide this menu item temporarily as per the ticket requirement. */}
                        {/* <Menu.Item className={'menu-group-item'} key='/dashboard/kouteki/specialist-review'>
                            <NavLink to='/dashboard/kouteki/specialist-review'>評価</NavLink>
                        </Menu.Item> */}
                    </SubMenu>
                    <SubMenu
                        key={'dashboard-wr'}
                        className={'menu-group'}
                        title={
                            <div className={'menu-group-title'}>
                                <FileTextOutlined />
                                <span className={'menu-group-title-text'}>就業規則診断メニュー</span>
                            </div>
                        }
                    >
                        <Menu.Item className={'menu-group-item'} key='/dashboard/working-regulation'>
                            <NavLink to='/dashboard/working-regulation'>診断申込管理</NavLink>
                        </Menu.Item>
                        <Menu.Item className={'menu-group-item'} key='/dashboard/working-regulation/meetings'>
                            <NavLink to='/dashboard/working-regulation/meetings'>面談依頼一覧</NavLink>
                        </Menu.Item>
                        <Menu.Item className='menu-group-item menu-name-too-long' key='/kisoku'>
                            <NavLink to='/kisoku#regulations-content'>
                                <div className={'menu-group-item-content'}>
                                    <span className={'menu-group-item-text'}>各種規程ひな形掲載一覧</span>
                                    <img className={'menu-group-item-icon'} width={14} height={14} src={ExportIcon} alt='ExportIcon' />
                                </div>
                            </NavLink>
                        </Menu.Item>
                        {/* Hide this menu item temporarily as per the ticket requirement. */}
                        {/* <Menu.Item className={'menu-group-item'} key='/dashboard/working-regulation/specialist-review'>
                            <NavLink to='/dashboard/working-regulation/specialist-review'>評価</NavLink>
                        </Menu.Item> */}
                    </SubMenu>
                </Menu>

                <Menu
                    className={'menu-layout menu-2'}
                    key={'dashboard-layout'}
                    mode='inline'
                    selectedKeys={[selectedKey]}
                    openKeys={openKeys}
                    onOpenChange={onOpenChange}
                >
                    <Menu.Item key='/dashboard/management' icon={<LayoutOutlined />}>
                        <NavLink to='/dashboard/management'>登録プロフィール</NavLink>
                    </Menu.Item>
                    <Menu.Item key='/dashboard/services' icon={<QuestionCircleOutlined />}>
                        <Link to='/dashboard/services'>サービスの使い方</Link>
                    </Menu.Item>
                    <Menu.Item key={'logout'} onClick={onOpenConfirmLogout} icon={<LogoutOutlined />}>
                        ログアウト
                    </Menu.Item>
                </Menu>
            </Sider>

            <CustomModal
                isVisible={isConfirmLogoutVisible}
                onClose={onCloseConfirmLogout}
                title='ログアウト'
                icon={questionIcon}
                text='ログアウトしてもよろしいですか'
                showCancelButton={true}
                cancelButtonText='いいえ'
                showConfirmButton={true}
                confirmButtonText='はい'
                confirmOnClick={onLogout}
                cancelOnClick={onCloseConfirmLogout}
            />
        </>
    );
};

export default Sidebar;
